/**
 * Protected-first, best-fit byte-budget selection — a faithful port of the
 * sister platform's `byte_budget.py` walker (ADR-075 Rule 5: whole memories
 * only, never mid-memory truncation; ported values, not re-derived).
 *
 * Parameterized on callbacks (`costFn` / `isProtectedFn` / `recencyFn`) and
 * `maxBytes` exactly like the source, so it has no import-time dependency on
 * the provider types module and serves any record shape.
 */

/**
 * Per-memory rendering overhead in the relative-chronological format:
 * `- [time_ago] {content} (category, confidence, disputed)\n` — the ported
 * conservative upper bound (worst-case literals ≈ 60 bytes), which makes the
 * byte-cap strict rather than lax.
 */
export const RENDER_OVERHEAD_PER_MEMORY_BYTES = 60;

/** Estimated rendered byte cost of one memory: UTF-8 content + overhead. */
export function utf8RenderCost(content: string): number {
  return Buffer.byteLength(content, 'utf-8') + RENDER_OVERHEAD_PER_MEMORY_BYTES;
}

export interface SelectionResult<T> {
  /** Kept records in protected-first / recency order (the walk's sort). */
  kept: T[];
  memoryCountDropped: number;
  protectedCountDropped: number;
}

export interface SelectionCallbacks<T> {
  costFn: (record: T) => number;
  /** Called only after admission, for costs shared by selected records. */
  onKeepFn?: (record: T) => void;
  isProtectedFn: (record: T) => boolean;
  /** POSIX/epoch milliseconds; larger = more recent. */
  recencyFn: (record: T) => number;
}

/**
 * Drop records until the estimated rendered size fits `maxBytes`.
 *
 * Sort: protected first (a year-old safety memory must not be dropped for a
 * recent coping entry), then most-recent first within each tier. Best-fit,
 * not first-fit: an oversize record is skipped — not a stop signal — so
 * smaller records later in the list can still fill the remaining budget. If
 * protected records alone exceed the budget, the oldest protected records
 * are still dropped — the budget is a hard ceiling, never violated.
 */
export function selectWithinByteBudget<T>(
  records: readonly T[],
  maxBytes: number,
  { costFn, onKeepFn, isProtectedFn, recencyFn }: SelectionCallbacks<T>,
): SelectionResult<T> {
  const sorted = [...records].sort((a, b) => {
    const rankA = isProtectedFn(a) ? 0 : 1;
    const rankB = isProtectedFn(b) ? 0 : 1;
    if (rankA !== rankB) return rankA - rankB;
    return recencyFn(b) - recencyFn(a);
  });

  const kept: T[] = [];
  let running = 0;
  for (const record of sorted) {
    const cost = costFn(record);
    if (running + cost > maxBytes) continue;
    kept.push(record);
    running += cost;
    onKeepFn?.(record);
  }

  const protectedTotal = records.filter(isProtectedFn).length;
  const protectedKept = kept.filter(isProtectedFn).length;
  return {
    kept,
    memoryCountDropped: records.length - kept.length,
    protectedCountDropped: protectedTotal - protectedKept,
  };
}
